import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Card,
    CardContent,
    CardTitle,
    Text,
    Row,
    Col,
    Form,
    Input,
    Button,
    ItemList,
    Badge,
    Popup,
    PopupTitle,
    PopupContent,
    FormItem,
    PopupFooter,
    PopupYesNo,
    Toast,
    FormButtons,
    TabPane,
    Tabs
} from "@tv2no/ui-react";
import { taxSearch, taxFilter } from "../../../api/services/taxSearch";
import {
    createPersonListEntry,
    movePersonListEntry,
    updatePersonListEntry,
    deletePersonListEntry
} from "../../../api/services/personListEntry";
import { personFullname } from "../../../utils/personUtils";
import { PersonSearchForm } from "../../../components/form/PersonSearchForm";
import { FilterForm } from "../../../components/form/FilterForm";

const Persons = ({ listEntries, yearList, personList, fetchPersonListEntries }) => {
    const { listId } = useParams();
    const [busySearching, setBusySearching] = useState(false);
    const [busyAddingEntry, setBusyAddingEntry] = useState(false);
    const [selectedListEntry, setSelectedListEntry] = useState(null);
    const [busyMovingEntry, setBusyMovingEntry] = useState(false);
    const [busyUpdatingEntry, setBusyUpdatingEntry] = useState(false);
    const [deleteListEntry, setDeleteListEntry] = useState(null);
    const [searchResult, setSearchResult] = useState(null);
    const [selectedSearchYear, setSelectedSearchYear] = useState(null);
    const [activeKey, setActiveKey] = useState(1);

    const searchRecords = ({ year, size , firstName, middleName, lastName, birthYear }) => {
        setSelectedSearchYear(year);
        setBusySearching(true);
        taxSearch(year || personList.taxYear, {
            firstName,
            middleName,
            lastName,
            birthYear,
            size
        })
            .then((res) => {
                setSearchResult(res);
                setBusySearching(false);
            })
            .catch((error) => {
                setBusySearching(false);
            });
    };

    const filterRecords = ({ sortBy, sortOrder, birthYearFilters, municipality, size, offset }) => {
        setBusySearching(true);
        taxFilter(personList.taxYear, {
            sortBy,
            sortOrder,
            birthYearFilters,
            municipality,
            size,
            offset
        })
            .then((res) => {
                setSearchResult(res);
                setBusySearching(false);
            })
            .catch((error) => {
                setBusySearching(false);
            });
    };

    const tabs = [{
        label: "Person søk"
    }, {
        label: "Filtrering"
    }];

    const handleAddPerson = (person) => {
        // Check if already added
        if (listEntries && listEntries.length) {
            for (let i = 0; i < listEntries.length; i++) {
                const entry = listEntries[i];
                if (
                    entry.firstName === person.firstName &&
                    entry.lastName === person.lastName &&
                    entry.birthYear === person.birthYear
                ) {
                    Toast.error(`${personFullname(person)} er allerede lagt til i listen`);
                    return;
                }
            }
        }
        setBusyAddingEntry(true);
        createPersonListEntry(listId, {
            sourceYear: selectedSearchYear || personList.taxYear,
            firstName: person.firstName,
            middleName: person.middleName,
            lastName: person.lastName,
            birthYear: person.birthYear,
            postCode: person.postCode,
            postName: person.postName,
            income: person.income,
            fortune: person.fortune,
            tax: person.tax,
            municipalities: Object.keys(person.municipalities).join(";")
        }).then(() => {
            // Reload entry list
            fetchPersonListEntries(() => {
                // Success message
                Toast.success(`${personFullname(person)} ble lagt til`);

                // Busy off
                setBusyAddingEntry(false);
            });
        });
    };

    const handleMovePerson = ({ oldIndex, newIndex }) => {
        // Check if custom sort
        if (personList.sorting !== "CUSTOM") {
            return Toast.warning("Velg  først at listen skal sorteres på rangering");
        }

        // Busy on
        setBusyMovingEntry(true);

        // Send request
        movePersonListEntry(personList.id, oldIndex, newIndex).then(() => {
            // Reload list
            fetchPersonListEntries(() => {
                // Busy off
                setBusyMovingEntry(false);
            });
        });
    };

    const handleUpdatePerson = () => {
        // Busy on
        setBusyUpdatingEntry(true);

        updatePersonListEntry(personList.id, selectedListEntry.id, {
            nickName: selectedListEntry.nickName
        }).then(() => {
            fetchPersonListEntries(() => {
                setSelectedListEntry(null);
                setBusyUpdatingEntry(false);
            });
        });
    };

    const handleDeletePerson = () =>
        deletePersonListEntry(personList.id, deleteListEntry.id).then(() => {
            fetchPersonListEntries(() => {
                setDeleteListEntry(null);
                Toast.success(`${personFullname(deleteListEntry)} ble fjernet fra listen`);
            });
        });

    const sortEntries = (a, b) => {
        switch (personList.sorting) {
            case "FORTUNE":
                return a.fortune > b.fortune ? -1 : b.fortune > a.fortune ? 1 : 0;
            case "TAX":
                return a.tax > b.tax ? -1 : b.tax > a.tax ? 1 : 0;
            case "CUSTOM":
                return a.orderIndex > b.orderIndex ? 1 : b.orderIndex > a.orderIndex ? -1 : 0;
            // INCOME
            default:
                return a.income > b.income ? -1 : b.income > a.income ? 1 : 0;
        }
    };

    return (
        <React.Fragment>
            <Row>
                <Col span={24}>
                    <Card>
                        <CardTitle title="Søk etter personer" />
                        <Tabs activeKey={activeKey} onChange={setActiveKey}>
                        {tabs.map((tab, index) => ( <TabPane key={index} tabKey={index + 1} label={tab.label}>
                        <CardContent>
                        {activeKey === 1 ? (
                            <PersonSearchForm
                                selectedYear={personList?.taxYear}
                                onSearch={searchRecords}
                                busy={busySearching}
                            />
                        ) : (
                            <FilterForm
                                selectedYear={personList?.taxYear}
                                onFilter={filterRecords}
                                busy={busySearching}
                            />
                        )}
                        </CardContent>
                        </TabPane>))}
                        </Tabs>
                    </Card>
                </Col>
                <Col span={24}>
                    <Card>
                        <CardTitle title="Personer" />
                        <CardContent>
                            <Row>
                                <Col sm={12}>
                                    <Row>
                                        <Col span={24}>
                                            <Text size="medium" weight="medium">
                                                Søkeresultat
                                            </Text>
                                            <Badge fill number color="primary" spacing="left">
                                                {searchResult?.total || 0}
                                            </Badge>
                                        </Col>
                                        <Col span={24}>
                                            <ItemList
                                                busy={busySearching}
                                                emptyMessage={
                                                    <Text opacity={40}>
                                                        Søk etter personer å legge til i listen...
                                                    </Text>
                                                }
                                            >
                                                {searchResult?.content?.map((person, index) => {
                                                    return (
                                                        <ItemList.Item
                                                            index={index}
                                                            key={person.id}
                                                            append={
                                                                <Button
                                                                    onClick={() =>
                                                                        handleAddPerson(person)
                                                                    }
                                                                    disabled={busyAddingEntry}
                                                                    square
                                                                    size="small"
                                                                    icon="plus"
                                                                    iconColor="success"
                                                                />
                                                            }
                                                        >
                                                            <Text truncate block>
                                                                <Text weight="medium">
                                                                    {personFullname(person)}
                                                                </Text>
                                                                <Text opacity={80}>
                                                                    , f. {person.birthYear},{" "}
                                                                    {person.postName}
                                                                </Text>
                                                            </Text>
                                                            <Text
                                                                size="small"
                                                                opacity={40}
                                                                block
                                                                truncate
                                                            >
                                                                Inntekt: {person.income} - Formue:{" "}
                                                                {person.fortune} - Skatt:{" "}
                                                                {person.tax}
                                                            </Text>
                                                        </ItemList.Item>
                                                    );
                                                })}
                                            </ItemList>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col sm={12}>
                                    <Row>
                                        <Col span={24}>
                                            <Text size="medium" weight="medium">
                                                Personer i listen
                                            </Text>
                                            <Badge fill number color="primary" spacing="left">
                                                {listEntries?.length || 0}
                                            </Badge>
                                        </Col>
                                        <Col span={24}>
                                            <ItemList
                                                busy={busyAddingEntry || busyMovingEntry}
                                                onSort={handleMovePerson}
                                                onItemClick={(val) => setSelectedListEntry(val)}
                                            >
                                                {listEntries?.sort(sortEntries).map((person) => (
                                                    <ItemList.Item
                                                        key={person.id}
                                                        value={person}
                                                        index={person.orderIndex}
                                                        append={[
                                                            person.status === "MISSING" ? (
                                                                <Badge
                                                                    leftSpacing
                                                                    fill
                                                                    size="small"
                                                                    color="error"
                                                                >
                                                                    Mangler
                                                                </Badge>
                                                            ) : person.status === "MULTIPLE" ? (
                                                                <Badge
                                                                    leftSpacing
                                                                    fill
                                                                    size="small"
                                                                    color="warning"
                                                                >
                                                                    Flere treff
                                                                </Badge>
                                                            ) : person.status === "MATCHED" ? (
                                                                <Badge
                                                                    leftSpacing
                                                                    fill
                                                                    size="small"
                                                                    color="success"
                                                                >
                                                                    Matchet
                                                                </Badge>
                                                            ) : person.status === "MANUAL" ? (
                                                                <Badge
                                                                    leftSpacing
                                                                    fill
                                                                    size="small"
                                                                    color="success"
                                                                >
                                                                    Manuell
                                                                </Badge>
                                                            ) : (
                                                                <span />
                                                            ),
                                                            <Button
                                                                onClick={() =>
                                                                    setDeleteListEntry(person)
                                                                }
                                                                icon="close"
                                                                iconColor="error"
                                                                square
                                                                size="small"
                                                            />
                                                        ]}
                                                    >
                                                        <Text truncate block>
                                                            <Text weight="medium">
                                                                {personFullname(person)}
                                                            </Text>
                                                            <Text opacity={80}>
                                                                , f. {person.birthYear},{" "}
                                                                {person.postName}
                                                            </Text>
                                                        </Text>
                                                        <Text
                                                            size="small"
                                                            opacity={40}
                                                            block
                                                            truncate
                                                        >
                                                            Inntekt: {person.income} - Formue:{" "}
                                                            {person.fortune} - Skatt: {person.tax}
                                                        </Text>
                                                    </ItemList.Item>
                                                ))}
                                            </ItemList>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </CardContent>
                    </Card>
                </Col>
            </Row>

            {/* Edit entry */}
            {selectedListEntry ? (
                <Popup>
                    <PopupTitle>{personFullname(selectedListEntry)}</PopupTitle>
                    <PopupContent>
                        <Form>
                            <FormItem label="Kallenavn">
                                <Input
                                    value={selectedListEntry.nickName}
                                    onChange={(val) =>
                                        setSelectedListEntry({
                                            ...selectedListEntry,
                                            nickName: val
                                        })
                                    }
                                />
                            </FormItem>
                        </Form>
                    </PopupContent>
                    <PopupFooter>
                        <FormButtons>
                            <Button
                                type="submit"
                                color="primary"
                                busy={busyUpdatingEntry}
                                onClick={handleUpdatePerson}
                            >
                                Lagre oppføringen
                            </Button>
                            <Button
                                onClick={() => setSelectedListEntry(null)}
                                disabled={busyUpdatingEntry}
                            >
                                Avbryt
                            </Button>
                        </FormButtons>
                    </PopupFooter>
                </Popup>
            ) : null}

            {/* Delete list entry confirmation */}
            {deleteListEntry ? (
                <PopupYesNo
                    title="Fjerne person fra listen?"
                    onYes={handleDeletePerson}
                    onNo={() => setDeleteListEntry(null)}
                >
                    {`Er du sikker på at du vil fjerne ${personFullname(
                        deleteListEntry
                    )} fra listen?`}
                </PopupYesNo>
            ) : null}
        </React.Fragment>
    );
};

export default Persons;
